import { useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fieldSurface } from '@datahub/design-system'
import { get } from '../../lib/api'

export interface CoreCompanyMatch {
  slug: string
  name: string
  websiteUrl: string | null
  country: string | null
  sector: string | null
  foundingYear: number | null
}

export interface CoreCompanyPrefill {
  slug: string
  displayName: string
  legalCompanyName: string
  aliases: string[]
  websiteUrl: string | null
  hqAddress: string | null
  countryOfIncorporation: string | null
  countriesOfOperation: string[]
  foundingYear: number | null
  sector: string | null
  subSectors: string[]
  ownershipStatus: string | null
  companyStatus: string | null
  employeeCount: string | null
  typeIfBanking: string | null
  coreCompanyId: string | null
  description: string | null
}

const MIN_TERM = 2
const DEBOUNCE_MS = 250

/** Waits for typing to settle so a lookup runs per pause, not per keystroke. */
function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

interface StearsCompanyPickerProps {
  onPick: (company: CoreCompanyPrefill) => void
  disabled?: boolean
}

/**
 * Type-ahead over the companies Stears already holds (CMP-01). Picking one pulls its
 * Core profile through the API — the Core credentials live there, never in the browser —
 * and hands it to the caller to pre-fill the new-company form.
 *
 * Implemented as a combobox rather than with the design system's TextInput because it
 * needs arrow-key navigation and the aria wiring that goes with a listbox; the field
 * itself is drawn with the same tokens so it matches every other input on the form.
 */
export function StearsCompanyPicker({ onPick, disabled }: StearsCompanyPickerProps) {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [focused, setFocused] = useState(false)
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const listId = useId()
  const inputId = useId()
  const debounced = useDebounced(term.trim(), DEBOUNCE_MS)
  const enabled = debounced.length >= MIN_TERM && !disabled

  const { data, isFetching } = useQuery({
    queryKey: ['stears-companies', debounced],
    queryFn: () => get<{ total: number; items: CoreCompanyMatch[] }>(
      `/admin/stears/companies?q=${encodeURIComponent(debounced)}`,
    ),
    enabled,
    // Keeping the last list on screen stops the dropdown flickering empty between keystrokes.
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  })

  const items = enabled ? (data?.items ?? []) : []

  useEffect(() => { setActive(0) }, [debounced])

  // A click anywhere else closes the list; the picker is inside a modal, so this
  // listener has to be on the document rather than a blur handler, which would fire
  // before the option's own click.
  useEffect(() => {
    if (!open) return
    const onDocumentClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocumentClick)
    return () => document.removeEventListener('mousedown', onDocumentClick)
  }, [open])

  async function choose(match: CoreCompanyMatch) {
    setLoadingSlug(match.slug)
    setError(null)
    try {
      const res = await get<{ company: CoreCompanyPrefill }>(`/admin/stears/companies/${encodeURIComponent(match.slug)}`)
      onPick(res.company)
      setTerm('')
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that company from Stears')
    } finally {
      setLoadingSlug(null)
    }
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') { setOpen(false); return }
    if (items.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => (i + 1) % items.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => (i - 1 + items.length) % items.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const match = items[active]
      if (match) void choose(match)
    }
  }

  const { borderColor, backgroundColor } = fieldSurface({ focused, error: Boolean(error) })
  const showList = open && enabled && (items.length > 0 || !isFetching)

  return (
    <div className="flex flex-col" style={{ gap: 8 }} ref={containerRef}>
      <label htmlFor={inputId} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        Find this company on Stears
      </label>

      <div style={{ position: 'relative' }}>
        <div
          className="flex items-center"
          style={{
            height: 40, backgroundColor, border: `0.5px solid ${borderColor}`,
            paddingLeft: 12, paddingRight: 12, gap: 8,
            opacity: disabled ? 0.6 : 1, transition: 'background-color 0.15s ease',
          }}
        >
          <input
            id={inputId}
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={showList && items[active] ? `${listId}-${active}` : undefined}
            autoComplete="off"
            value={term}
            disabled={disabled}
            placeholder="Start typing a company name"
            onChange={(e) => { setTerm(e.target.value); setOpen(true); setError(null) }}
            onFocus={() => { setFocused(true); setOpen(true) }}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
            className="font-mulish text-white w-full"
            style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 12, lineHeight: '16px' }}
          />
          {isFetching && enabled && (
            <span className="font-mulish text-text-support flex-shrink-0" style={{ fontSize: 10 }}>Searching…</span>
          )}
        </div>

        {showList && (
          <ul
            id={listId}
            role="listbox"
            aria-label="Companies on Stears"
            className="bg-background-main"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
              margin: 0, padding: 0, listStyle: 'none', maxHeight: 240, overflowY: 'auto',
              border: '0.5px solid #4B5563', borderTop: 'none',
            }}
          >
            {items.length === 0 ? (
              <li className="font-mulish text-text-support" style={{ padding: '10px 12px', fontSize: 12 }}>
                Nothing on Stears matches “{debounced}”. Enter the details by hand.
              </li>
            ) : (
              items.map((match, index) => (
                <li
                  key={match.slug}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => void choose(match)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer',
                    borderBottom: '0.5px solid #4B5563',
                    background: index === active ? '#2D3036' : 'transparent',
                  }}
                >
                  <div className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '18px' }}>
                    {match.name}
                    {loadingSlug === match.slug && <span className="text-text-support"> · loading…</span>}
                  </div>
                  <div className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '14px' }}>
                    {[match.sector, match.country, match.foundingYear ? `founded ${match.foundingYear}` : null]
                      .filter(Boolean)
                      .join(' · ') || 'No further detail on Stears'}
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {error ? (
        <span className="font-mulish" style={{ fontSize: 10, lineHeight: '16px', color: '#F05042' }}>{error}</span>
      ) : (
        <span className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px' }}>
          Optional. Picking a company fills in what Stears already holds; you can still edit everything below.
        </span>
      )}
    </div>
  )
}
